import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

type TBrowserBack = {
  handleBack: () => void;
  previousPage: string | null;
  setPreviousPage: Dispatch<SetStateAction<string | null>>;
  setNavigateTo: Dispatch<SetStateAction<string | null>>;
};

const BrowserBackContext = createContext(null);

export function useBrowserBack(): TBrowserBack {
  return useContext(BrowserBackContext as any);
}

export default function BrowserBackProvider({ children }: any) {
  const location = useLocation();
  const navigate = useNavigate();

  const [previousPage, setPreviousPage] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<string | null>(
    location.pathname,
  );
  const [previousSearchParams, setPreviousSearchParams] = useState<
    string | null
  >(null);
  const [currentSearchParams, setCurrentSearchParams] = useState<string>(
    location.search,
  );

  const [navigateTo, setNavigateTo] = useState<string | null>(null);

  useEffect(() => {
    if (location.pathname != currentPage) {
      setPreviousPage(currentPage);
      setCurrentPage(location.pathname);
    }

    setPreviousSearchParams(currentSearchParams);
    setCurrentSearchParams(location.search);
  }, [location.pathname]);

  useEffect(() => {
    setNavigateTo(navigateTo);
  }, [navigateTo]);

  const handleBack = useCallback(() => {
    if (navigateTo) {
      navigate(navigateTo, { state: location.state });
    } else if (previousPage) {
      navigate(`${previousPage}${previousSearchParams}`, {
        state: location.state,
      });
    } else {
      navigate(-1);
    }
  }, [navigateTo, location, previousPage, previousSearchParams]);

  useEffect(() => {
    window.addEventListener("popstate", handleBack);
    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, [handleBack]);

  const contextValue = {
    previousPage,
    setPreviousPage,
    setNavigateTo,
    handleBack,
  };

  return (
    <BrowserBackContext.Provider value={contextValue}>
      {children}
    </BrowserBackContext.Provider>
  );
}
